import { useState } from 'react'
import { motion, useTransform } from 'framer-motion'
import balance from 'balanced-line-split'
import { useWindowSize } from 'lib/hooks/use-window'
import { maxWidth } from 'constants/styles'
import { headers as ease, parallax } from 'constants/easings'
import Image from 'components/Image'
import { useScrollerMotion } from 'scroller-motion'

type Props = {
	title: string
	lines?: number
	bigImage?: string
	smallImage?: string
}

export default function Header({
	title,
	lines = 2,
	bigImage,
	smallImage,
}: Props ) {
	const { scrollY } = useScrollerMotion()
	const { height } = useWindowSize()
	const y = useTransform( scrollY, [ 0, height * 2 ], [ '0%', '-10%' ] )
	const image = useTransform( scrollY, [ 0, height * 2 ], [ 0, -260 ] )
	const subtle = useTransform( scrollY, [ 0, height * 2 ], [ '0%', `-${parallax}%` ] )

	return (
		<header>
			<div className="container">
				<H1 title={title} lines={lines} />
			</div>

			<motion.picture
				className="big"
				initial={{ y: 100, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				// exit={{ y: 100, opacity: 0 }}
				transition={{ duration: .95, ease }}
			>
				<Image
					src={bigImage}
					initial={{ scale: 1.1, y: '5%' }}
					animate={{ y: 0 }}
					// exit={{ y: '5%' }}
					transition={{ duration: .95, ease }}
					style={{ y: subtle }}
				/>
			</motion.picture>
			<div className="small container">
				<div>
					<motion.picture
						className="small"
						initial={{ y: 200, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						// exit={{ y: 200, opacity: 0 }}
						// exit={{ opacity: 0 }}
						transition={{ duration: .95, ease }}
						style={{ y: image }}
					>
						<Image
							src={smallImage}
							sizes={`${Math.ceil( 434 * 1.1 * 1.5 /* still too low-res in case of other aspect ratios */ )}px`}
							style={{ scale: 1.1, y }}
							initial={{ y: '3%' }}
							animate={{ y: 0 }}
							// exit={{ y: '3%' }}
							transition={{ duration: .95, ease }}
						/>
					</motion.picture>
				</div>
			</div>

			<style jsx>{`
				.small.container > div {
					height: 300px;
					position: relative;
				}
				header {
					margin-bottom: -10vw;
				}
				header :global( picture.big ) {
					margin-top: 15vh;
					margin-left: calc( 50% - 50vw );
					width: calc( ( 100% - ${maxWidth}px ) / 2 + ${maxWidth}px - ${( maxWidth / 12 ) * 2}px );
					height: 80vh;
				}
				header :global( picture.small ) {
					position: absolute;
					right: 0;
					bottom: 0;
					width: 35vw;
					height: calc( 35vw * 1.62 );
					max-width: 434px;
					max-height: calc( 434px * 1.62 );
					z-index: 1;
				}
				@media ( max-height: 800px ) {
					header :global( picture.small ) {
						width: 25vw;
						height: calc( 25vw * 1.62 );
						right: 5vw;
					}
				}
				@media ( max-width: 900px ) {
					header {
						margin-bottom: 50px;
					}
					header .small {
						display: none;
					}
					header :global( picture.big ) {
						height: 80vw;
					}
				}
			`}</style>
		</header>
	)
}

export function H1({ title, lines = 1, animate = true }) {
	const [ staggerDirection, setStaggerDirection ] = useState( 1 )
	return (
		<motion.h1
			style={{ maxWidth: 'none' }}
			onAnimationComplete={() => setStaggerDirection( -1 )}
			transition={{ staggerChildren: .1, staggerDirection }}
			initial={animate ? 'initial' : 'animate'}
			animate="animate"
			// exit="exit"
			variants={{
				animate: {},
				exit: {},
			}}
		>
			{balance( title, lines ).split( '\n' ).map( ( line, i ) => (
				<motion.span
					key={i}
					style={{ display: 'block' }}
					variants={{
						initial: { opacity: 0, y: 140, rotate: 10 },
						animate: { opacity: 1, y: 0, rotate: 0 },
						exit: { opacity: 0, y: 60, rotate: 0 },
					}}
					transition={( staggerDirection === 1 ? {
						opacity: { duration: 1.1, delay: 0.4, ease },
						default: { duration: 1.3, ease },
					} : {
						opacity: { duration: .6, ease },
						default: { duration: .95, ease },
					})}
				>
					{line}
				</motion.span>
			) )}
		</motion.h1>
	)
}
